import * as Sentry from '@sentry/react';
import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import { useUploadBenchmarkImageMutation } from '@vizit/api';
import { SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';
import type { UploadQueueFileInput } from '@vizit/organisms';
import { type S3UploadFields, uploadToS3WithProgress } from '@vizit/s3';
import * as React from 'react';

/** Refetches every image list of the benchmark and the benchmark itself, whatever their sort. */
function refreshBenchmark(queryClient: QueryClient, workspaceId: string, benchmarkId: string) {
  return queryClient.invalidateQueries({
    predicate: ({ queryKey: [name, variables] }) => {
      const scope = variables as { workspace_id?: string; benchmark_id?: string } | undefined;
      return (
        (name === 'ListBenchmarkImages' || name === 'GetBenchmark') &&
        scope?.workspace_id === workspaceId &&
        scope?.benchmark_id === benchmarkId
      );
    },
  });
}

function withMember(ids: ReadonlySet<string>, id: string, isMember: boolean): ReadonlySet<string> {
  if (ids.has(id) === isMember) {
    return ids;
  }
  const next = new Set(ids);
  if (isMember) {
    next.add(id);
  } else {
    next.delete(id);
  }
  return next;
}

interface UseBenchmarkImageUploadProps {
  workspaceId?: string;
  benchmarkId: string;
  /**
   * Sent with each upload when set. The API asks for one, but a benchmark has none until a category is added to it.
   */
  productCategoryId?: string;
}

/**
 * Turns chosen files into upload queue items for a benchmark. Each item creates the image record, refreshes the
 * benchmark so its tile shows up right away, then sends the file to S3 with the presigned POST the API returned.
 * `uploadingImageIds` and `failedImageIds` say which tiles are still uploading or never got their file. A failure is
 * reported to Sentry and rethrown so the upload queue marks the item failed.
 */
function useBenchmarkImageUpload({ workspaceId, benchmarkId, productCategoryId }: UseBenchmarkImageUploadProps) {
  const queryClient = useQueryClient();
  const { mutateAsync } = useUploadBenchmarkImageMutation();

  // The API cannot say when a file has landed, so the page learns it from the uploads it runs itself.
  const [uploadingImageIds, setUploadingImageIds] = React.useState<ReadonlySet<string>>(() => new Set());
  const [failedImageIds, setFailedImageIds] = React.useState<ReadonlySet<string>>(() => new Set());

  const markUpload = React.useCallback((imageId: string, state: 'uploading' | 'done' | 'failed') => {
    setUploadingImageIds((ids) => withMember(ids, imageId, state === 'uploading'));
    setFailedImageIds((ids) => withMember(ids, imageId, state === 'failed'));
  }, []);

  const uploadFile = React.useCallback(
    async (file: File, onProgress: (progress: number) => void) => {
      if (!workspaceId) {
        throw new Error('Cannot upload to a benchmark before the workspace is known.');
      }

      const extra = {
        file_name: file.name,
        file_size: file.size,
        workspace_id: workspaceId,
        benchmark_id: benchmarkId,
      };
      const tags = {
        feature: SENTRY_FEATURE_TAGS.Benchmark,
        stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Benchmark].benchmarkImageUpload,
      };

      try {
        const upload = await mutateAsync({
          workspace_id: workspaceId,
          benchmark_id: benchmarkId,
          name: file.name,
          product_category_id: productCategoryId,
        });

        // The record exists now, so its tile shows as processing while the file is on its way to S3.
        markUpload(upload.imageId, 'uploading');
        await refreshBenchmark(queryClient, workspaceId, benchmarkId);

        try {
          await uploadToS3WithProgress(
            upload.imageId,
            upload.uploadUrl,
            file,
            upload.uploadFields as unknown as S3UploadFields,
            onProgress,
            async () => markUpload(upload.imageId, 'done'),
            () => undefined,
          );
        } catch (error) {
          markUpload(upload.imageId, 'failed');
          throw error;
        }
      } catch (error) {
        Sentry.captureException(error, { level: 'error', tags, extra });
        throw error;
      }
    },
    [workspaceId, benchmarkId, productCategoryId, mutateAsync, queryClient, markUpload],
  );

  const toUploadQueueItems = React.useCallback(
    (files: File[]): UploadQueueFileInput[] =>
      files.map((file) => ({ file, onUpload: (onProgress) => uploadFile(file, onProgress) })),
    [uploadFile],
  );

  return { toUploadQueueItems, uploadingImageIds, failedImageIds };
}

export { useBenchmarkImageUpload };
